// IEA Site Survey screens, step 2 shell. Pure: state in, HTML out.
// Mockup: MOCKUP_IEA_SHELL.html (approved 2026-10-14). Words: IEA_OFFLINE_SHELL_WORDS.md rev 3.
// Actions use data-sv so they never collide with the admin pages' data-act.

import { assetHref } from "./asset-page";
import { sitePicker } from "./site-picker";
import { esc } from "./training-screens";
import {
  housingOf,
  isUtility,
  phaseOf,
  positionWords,
  typicalFor,
  voltsWords,
  waysOutOf,
  type CheckResult,
  type PackAsset,
  type SitePack,
} from "./survey";

export interface SiteChoice {
  siteNumber: string;
  name: string;
  assetCount: number | null;
  packSavedAt: string | null;
}

export interface StripState {
  online: boolean;
  waiting: number;
}

const pill = (tone: string, text: string) => `<span class="pill ${tone}">${esc(text)}</span>`;

function row(label: string, value: string, end = ""): string {
  return `<div class="sv-row"><b>${esc(label)}</b><span>${value}</span><span class="sv-end">${end}</span></div>`;
}

/** The trail under the shared header. Site name first, never the number. */
export function trail(parts: { label: string; href?: string }[], withScan: boolean): string {
  const crumbs = parts
    .map((p, i) => (p.href && i < parts.length - 1 ? `<a href="${p.href}">${esc(p.label)}</a>` : `<b>${esc(p.label)}</b>`))
    .join('<span class="sv-sep">›</span>');
  const scan = withScan ? '<button class="sv-scanbtn" type="button" data-sv="scan">▣ Scan</button>' : "";
  return `<div class="sv-trail"><div class="sv-crumbs">${crumbs}</div>${scan}</div>`;
}

/** One thin line on survey pages only: signal, waiting count, Aa. */
export function strip(s: StripState): string {
  return `<div class="sv-strip" data-sv="outbox"><span class="sv-net"><span class="sv-dot ${s.online ? "on" : ""}"></span>${s.online ? "Online" : "Offline"}</span>
    <span class="sv-wait">${s.waiting ? `${s.waiting} waiting` : ""}</span>
    <button class="sv-aa" type="button" data-sv="aa">Aa</button></div>`;
}

/** The badge has no IEA Site Survey switch. */
export function needsSwitch(): string {
  return `<div class="note red">Your badge needs the IEA Site Survey switch. People &amp; Badges, your admin badge, Field Pages, Turn On. Then close the app and open it with signal.</div>`;
}

/** IEA Survey site list: the site picker (MOCKUP_SITE_PICKER.html rev 2). */
export function siteList(sites: SiteChoice[]): string {
  if (!sites.length) return '<div class="note">No sites on this device yet. Open the survey once with signal.</div>';
  return sitePicker({
    id: "survey",
    title: "Pick a site",
    sub: "Only sites your badge covers. Site pack status shows on each.",
    filters: [{ key: "nopack", label: "No Site Pack" }],
    sites: sites.map((s) => ({
      siteNumber: s.siteNumber,
      name: s.name,
      href: `#/survey/${s.siteNumber}`,
      sub: s.assetCount === null ? "Assets: open with signal to count" : `${s.assetCount} assets`,
      pills: s.packSavedAt ? pill("green", "Site pack saved") : pill("grey", "No site pack"),
      onDevice: Boolean(s.packSavedAt),
      flags: s.packSavedAt ? [] : ["nopack"],
    })),
  });
}

/**
 * Asset ID Creator site list: the site picker (MOCKUP_SITE_PICKER.html rev 2).
 * `hasFolder` is null while Drive is not connected: no No Folder chip then.
 */
export function creatorSiteList(sites: (SiteChoice & { waiting: number; drivePill?: string; hasFolder?: boolean | null })[], driveLine = ""): string {
  if (!sites.length) return '<div class="note">No sites on this device yet. Open the Assessment Sheet once with signal.</div>';
  const drive = sites.some((s) => typeof s.hasFolder === "boolean");
  return sitePicker({
    id: "assets",
    above: driveLine,
    title: "Pick a site",
    sub: "Sites your badge covers. Tap one and its Assessment Sheet opens.",
    filters: [...(drive ? [{ key: "nofolder", label: "No Folder" }] : []), { key: "waiting", label: "Waiting" }, { key: "nopack", label: "No Site Pack" }],
    sites: sites.map((s) => ({
      siteNumber: s.siteNumber,
      name: s.name,
      href: `#/assets/${s.siteNumber}`,
      sub: s.assetCount === null ? "Assets counted when the pack is on this iPad" : `${s.assetCount} assets`,
      pills: `${s.waiting ? pill("amber", `${s.waiting} waiting`) + " " : ""}${s.packSavedAt ? pill("green", "Site pack saved") : pill("grey", "No site pack")}${s.drivePill ? ` ${s.drivePill}` : ""}`,
      onDevice: Boolean(s.packSavedAt),
      flags: [...(s.hasFolder === false ? ["nofolder"] : []), ...(s.waiting ? ["waiting"] : []), ...(s.packSavedAt ? [] : ["nopack"])],
    })),
  });
}

export interface SitePageState {
  siteName: string;
  siteNumber: string;
  deviceName: string;
  check: CheckResult;
  online: boolean;
  /** The site pack on this device, or null. */
  pack: SitePack | null;
  counts: { total: number; ieaDone: number; notTraced: number; studyReady: number } | null;
  busy: string;
  error: string;
}

/** The one site page (IEA_SURVEY_ENTRY_WORDS.md section 5). Before you survey and the old survey page live here now. */
export function sitePage(v: SitePageState): string {
  const pack = v.pack;
  const blocked = v.check.blocks.length > 0;
  const head = `<div><h2 class="sv-h">${esc(v.siteName)}${pack ? " survey" : ""}</h2><div class="sv-sub">Device: <b>${esc(v.deviceName)}</b> <a class="sv-link" href="#" data-sv="name">Change</a>${pack ? " · Scan any label to open it." : ""}</div></div>`;
  const note = blocked ? `<div class="note red">${v.check.blocks.map(esc).join("<br>")}</div>` : "";
  const error = v.error ? `<div class="note red">${esc(v.error)}</div>` : "";
  const stats = v.counts
    ? `<div class="sv-stats">
      <div class="sv-stat"><div class="sv-big">${v.counts.ieaDone}</div><small>IEA done of ${v.counts.total}</small></div>
      <div class="sv-stat"><div class="sv-big red">${v.counts.notTraced}</div><small>Not traced to service</small></div>
      <div class="sv-stat"><div class="sv-big">${v.counts.studyReady}</div><small>Study ready</small></div>
    </div>`
    : "";
  const open = blocked || v.busy || !pack
    ? '<button class="sv-btn gold" type="button" disabled>Open Map</button>'
    : `<a class="sv-btn gold" href="#/survey/${esc(v.siteNumber)}/map">Open Map</a>`;
  // Asset ID Creator: the only place that makes an asset, its own section (ASSET_IDS_SECTION_WORDS.md section 10).
  const ids = `<a class="sv-btn line" href="#/assets/${esc(v.siteNumber)}">Assessment Sheet</a>`;
  const btns = `<div class="sv-btns">${open}${ids}<button class="sv-btn line" type="button" data-sv="pack"${!v.online || v.busy ? " disabled" : ""}>${v.busy === "pack" ? "Downloading…" : pack ? "Refresh Site Pack" : "Download Site Pack"}</button></div>`;
  let start = "";
  if (pack) {
    const utilities = pack.assets.filter(isUtility);
    const first = utilities[0];
    const others = utilities.slice(1);
    start = `<div class="box"><div class="sv-boxhd">Start here <small>· the survey traces down from the service</small></div>
      ${first ? row("Utility service", `<span class="mono">${esc(first.assetId)}</span> · ${esc(first.location)}`, `<a class="sv-btn line sm" href="#/survey/${pack.siteNumber}/a/${encodeURIComponent(first.assetId)}">Open</a>`) : row("Utility service", "None in the registry")}
      ${others.length ? row("Also on site", others.map((o) => `<a class="mono" href="#/survey/${pack.siteNumber}/a/${encodeURIComponent(o.assetId)}">${esc(o.assetId)}</a>`).join(", ")) : ""}
    </div>`;
  }
  // The utility service shows under Start here, so the check rows leave it out.
  const lines = v.check.lines
    .filter((l) => l.label !== "Utility service")
    .map((l) => tightRow(l.label, esc(l.text), pill(l.tone, l.pill)))
    .join("");
  const check = `<div class="box">${pack ? '<div class="sv-boxhd">Site check</div>' : ""}${lines}</div>`;
  return pack ? `${head}${note}${error}${stats}${btns}${start}${check}` : `${head}${note}${error}${check}${btns}`;
}

function tightRow(label: string, value: string, end = ""): string {
  return `<div class="sv-row tight"><b>${esc(label)}</b><span>${value}</span><span class="sv-end">${end}</span></div>`;
}

/** No site pack and signal: the pack downloads, then the Map opens. No button to find. */
export function packDownloading(siteName: string): string {
  return `<div class="sv-loading"><h2 class="sv-h">Downloading the ${esc(siteName)} site pack</h2>
    <div class="sv-sub">The Map opens when it is saved.</div>
    <div class="sv-bar"><i></i></div></div>`;
}

/** The asset summary a scan opens. Everything here is filled from the label and the registry. */
export function assetSummary(pack: SitePack, a: PackAsset, traced: boolean): string {
  const ids = new Set(pack.assets.map((x) => x.assetId));
  const housing = housingOf(a.assetId, ids);
  const ways = waysOutOf(a.assetId, pack.assets);
  const done = pack.ieaDone.includes(a.assetId);
  const utility = isUtility(a);
  const volts = voltsWords(a);
  const kind = utility ? "Utility service" : housing ? "Way out" : ways.length ? "Housing" : "Equipment";

  const rows = [
    row("Equipment", `${esc(a.equipmentType || "Unknown type")} · ${kind}`),
    row("Location", esc(a.location || "Not set")),
    row("Voltage", volts ? `${esc(volts)} · ${phaseOf(a.nominalVoltage)}` : "Not in the registry"),
    row("Class", esc(a.equipmentCode || "None")),
    housing ? row("Sits in", `<a class="mono" href="#/survey/${pack.siteNumber}/a/${encodeURIComponent(housing)}">${esc(housing)}</a>`) : "",
    housing ? row("Position", esc(positionWords(a.assetId, housing, a.equipmentCode))) : "",
    ways.length ? row("Ways out", `${ways.length} in the registry · ${ways.filter((w) => pack.ieaDone.includes(w.assetId)).length} done`) : "",
    utility ? row("Utility letter", pill("amber", "Not yet")) : "",
    a.isOutOfService ? row("Status", pill("grey", "Out of service")) : "",
    row("Last IEA", done ? "Saved" : "None"),
  ].join("");

  const t = utility ? null : typicalFor(a.equipmentCode, pack.ieeeClassOf[a.equipmentCode] ?? "", a.nominalVolts, Boolean(housing));
  const typical = utility
    ? row("Starts the map", "Everything below traces up to this service")
    : t
      ? row("Bus gap", `${t.gapMm} mm · ${esc(t.gapBasis)}`) + row("Working distance", `${t.workingDistanceIn} in · ${esc(t.distanceBasis)}`) + row("Electrode", `${t.electrode} · ${esc(t.electrodeBasis)}`)
      : row("Starting values", "Above 1000 V. Read from the nameplate in the field.");

  const traceNote = utility
    ? '<div class="note green">Top of the map. Tracing starts here once its card is saved.</div>'
    : traced
      ? '<div class="note green">Traced to the utility service.</div>'
      : '<div class="note red">Not traced. Map it from upstream. You can still record this equipment now.</div>';

  return `<div class="sv-title"><h2 class="sv-h mono">${esc(a.assetId)}</h2>${utility || traced ? pill("green", "Traced") : pill("red", "Not traced to service")}${pill(done ? "green" : "grey", done ? "IEA saved" : "IEA not started")}</div>
    ${traceNote}
    <div class="box"><div class="sv-boxhd">From the label and registry <small>· filled, not asked</small></div>${rows}</div>
    <div class="box"><div class="sv-boxhd">Starting values <small>· IEEE 1584 typical, confirmed on the cards</small></div>${typical}</div>
    <div class="sv-btns"><a class="sv-btn gold" href="#/survey/${pack.siteNumber}/map">Open Map</a><a class="sv-btn line" href="${assetHref(pack.siteNumber, a.assetId)}">Open Asset Page</a><button class="sv-btn line" type="button" data-sv="scan">Scan Next</button></div>`;
}

/** Full-screen scan, the same camera reader as the guest page and the bag tag. */
export function scanSheet(result: string): string {
  return `<div class="sv-sheet">
    <div class="sv-cam"><video id="sv-video" playsinline muted></video><div class="sv-fr"></div><div class="sv-camtx" id="sv-camtx">Starting the camera…</div></div>
    <form class="sv-type" data-sv-form="type"><input id="sv-typeid" autocomplete="off" autocapitalize="characters" placeholder="Or type the ID"><button class="sv-btn gold" type="submit">Open</button></form>
    ${result}
    <button class="sv-btn line" type="button" data-sv="scan-cancel">Cancel</button>
  </div>`;
}

export function scanResult(kind: string, assetId: string, otherSiteName: string, otherSite: string): string {
  if (kind === "notInRegistry")
    return `<div class="sv-result red"><b>Not in the registry.</b> <span class="mono">${esc(assetId)}</span> is not in this site pack. Refresh the site pack if it was just pushed. The survey never creates an asset.</div>`;
  if (kind === "otherSite")
    return `<div class="sv-result amber"><b>This label is at ${esc(otherSiteName)}.</b><div class="sv-btns"><a class="sv-btn gold" href="#/survey/${esc(otherSite)}">Switch Site</a></div></div>`;
  if (kind === "qew") return '<div class="sv-result amber"><b>That is a QEW tag.</b> Scan an asset label.</div>';
  if (kind === "notSafeTag") return '<div class="sv-result red"><b>That is not a SafeTag label.</b> Scan the QR on the equipment label.</div>';
  return "";
}

/**
 * Name this device. Change opens it with the current name and a Cancel that keeps it (NAV_AUDIT.md item 10).
 * The first time, the device has no name yet, so Save is the only way on.
 */
export function nameSheet(current: string): string {
  return `<div class="sv-modal"><form class="sv-card" data-sv-form="name">
    <b class="sv-cardt">Name this device</b>
    <span class="sv-sub">Saved on every record as Recorded On. Shown to other devices surveying this site.</span>
    <input id="sv-name" value="${esc(current)}" maxlength="40" autocomplete="off">
    <div class="sv-btns">${current ? '<button class="sv-btn line" type="button" data-sv="name-cancel">Cancel</button>' : ""}<button class="sv-btn gold" type="submit">Save</button></div>
  </form></div>`;
}

// ---- Outbox (IEA_OFFLINE_SHELL_WORDS.md section 12) ----

export interface OutboxView {
  online: boolean;
  counts: { waiting: number; sentToday: number; photosWaiting: number; refused: number };
  rows: { assetId: string; card: string; at: string; photos: number; state: string; reason: string }[];
  photosHeld: boolean;
  storage: { usedMb: number | null; freeMb: number | null; kept: boolean | null };
  deviceName: string;
  log: { at: string; text: string }[];
  busy: string;
  flash: string;
  error: string;
}

function mb(n: number | null): string {
  if (n === null) return "unknown";
  return n >= 1024 ? `${(n / 1024).toFixed(1)} GB` : `${Math.round(n)} MB`;
}

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export function outboxScreen(v: OutboxView): string {
  const list = v.rows.length
    ? `<div class="sv-boxhd">Waiting to send</div>${v.rows
        .map(
          (r) => `<div class="sv-wrow"><span><b class="mono">${esc(r.assetId)}</b> · ${esc(r.card)}${r.photos ? ` · ${r.photos} ${r.photos === 1 ? "photo" : "photos"}` : ""}${r.reason ? `<br><small class="sv-refused">${esc(r.reason)}</small>` : ""}</span>
      <span class="muted">${esc(clock(r.at))}</span>${r.state === "refused" ? pill("red", "Refused") : pill("amber", "Waiting")}</div>`,
        )
        .join("")}`
    : row("Waiting", "Nothing waiting. Everything is in the cloud.", pill("green", "All sent"));
  const kept = v.storage.kept === null ? "" : v.storage.kept ? pill("green", "Kept") : pill("amber", "Not kept");
  const busy = Boolean(v.busy);
  return `<h2 class="sv-h">Outbox</h2>
    <div class="sv-stats">
      <div class="sv-stat"><div class="sv-big">${v.counts.waiting}</div><small>Waiting</small></div>
      <div class="sv-stat"><div class="sv-big">${v.counts.sentToday}</div><small>Sent today</small></div>
      <div class="sv-stat"><div class="sv-big">${v.counts.photosWaiting}</div><small>Photos waiting</small></div>
    </div>
    ${v.flash ? `<div class="note green">${esc(v.flash)}</div>` : ""}
    ${v.error ? `<div class="note red">${esc(v.error)}</div>` : ""}
    <div class="box">${list}</div>
    <div class="sv-btns"><button class="sv-btn gold" type="button" data-sv="upload"${!v.online || busy || (!v.counts.waiting && !v.counts.photosWaiting) ? " disabled" : ""}>${v.busy === "upload" ? "Sending…" : "Upload All"}</button></div>
    ${v.online ? "" : '<div class="sv-sub">No signal. Everything waits on this device and sends when signal comes back.</div>'}
    <div class="box">
      ${row("Photos", `<span class="sv-seg"><button type="button" data-sv="photos-auto" class="${v.photosHeld ? "" : "on"}">Send When Online</button><button type="button" data-sv="photos-hold" class="${v.photosHeld ? "on" : ""}">Hold Until Upload All</button></span>`)}
      ${row("Storage", `${mb(v.storage.freeMb)} free · ${mb(v.storage.usedMb)} used`, kept)}
      ${row("Device", esc(v.deviceName || "Not named"), '<button class="sv-btn line sm" type="button" data-sv="name">Change</button>')}
      ${row(
        "Backup",
        "Every waiting record and photo, to Files",
        `<span class="sv-btns"><button class="sv-btn line sm" type="button" data-sv="export"${busy ? " disabled" : ""}>Export</button><label class="sv-btn line sm">Load<input type="file" accept=".json,application/json" data-sv-file="backup" hidden></label></span>`,
      )}
      ${row("Survey", v.counts.waiting ? "Send everything first. Then End Survey." : "End the survey once nothing is waiting", `<button class="sv-btn line sm" type="button" data-sv="end"${v.counts.waiting || busy ? " disabled" : ""}>End Survey</button>`)}
    </div>
    <div class="box"><div class="sv-boxhd">Sync log</div><div class="sv-log">${v.log.length ? v.log.map((l) => `${esc(clock(l.at))}  ${esc(l.text)}`).join("<br>") : "Nothing yet."}</div></div>`;
}
